import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, KeyboardSensor, PointerSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import { CalendarClock, HardHat } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ThumbStack } from '../../../../components/design-system/analytics';
import { RelativeTime, formatCurrency, formatNumber } from '../../../../components/design-system/admin';
import type { DeskCard as Card, LeadDeskStage } from '../../../../common/hooks/use-lead-desk-api.hook';
import { StageColumn, displayName, initialOf } from '../lead-desk-view';
import { LeadTierBadge } from './LeadTierBadge';

/**
 * The pipeline: one column per stage, a card per lead, dragged from one stage
 * to the next. A click (not a drag) opens the lead's file; the drag only
 * starts after a few pixels so a click stays a click.
 */
export function DeskBoard({
    columns,
    onMove,
    onOpen,
}: {
    columns: StageColumn[];
    onMove: (id: string, stage: LeadDeskStage) => void;
    onOpen: (id: string) => void;
}) {
    const { t } = useTranslation();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(KeyboardSensor));
    const [dragging, setDragging] = useState<Card | null>(null);

    const onDragStart = (e: DragStartEvent) => setDragging((e.active.data.current as any)?.card ?? null);
    const onDragEnd = (e: DragEndEvent) => {
        setDragging(null);
        const to = e.over?.id as LeadDeskStage | undefined;
        const card = (e.active.data.current as any)?.card as Card | undefined;
        if (to && card && card.stage !== to) onMove(card.id, to);
    };

    return (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setDragging(null)}>
            <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-3" data-testid="desk-board">
                {columns.map((column) => (
                    <Column key={column.stage} column={column} onOpen={onOpen} emptyLabel={t('LEAD_DESK.NO_COLUMN')} />
                ))}
            </div>
            <DragOverlay dropAnimation={null}>{dragging ? <LeadCardView card={dragging} lifted /> : null}</DragOverlay>
        </DndContext>
    );
}

function Column({ column, onOpen, emptyLabel }: { column: StageColumn; onOpen: (id: string) => void; emptyLabel: string }) {
    const { t } = useTranslation();
    const { setNodeRef, isOver } = useDroppable({ id: column.stage });
    return (
        <section
            ref={setNodeRef}
            aria-label={t(`LEAD_DESK.STAGE.${column.stage.toUpperCase()}`)}
            data-testid={`desk-column-${column.stage}`}
            className={cn('flex w-[272px] shrink-0 flex-col gap-2 rounded-xl bg-muted/50 p-2 transition-shadow', isOver && 'ring-1 ring-foreground/25')}
        >
            <header className="flex items-baseline justify-between gap-2 px-1.5 pt-1 pb-0.5">
                <h2 className="text-sm font-medium">
                    {t(`LEAD_DESK.STAGE.${column.stage.toUpperCase()}`)}
                    <span className="ms-1.5 tabular-nums text-muted-foreground">{formatNumber(column.cards.length)}</span>
                </h2>
                {column.value && <span className="text-xs tabular-nums text-muted-foreground">{formatCurrency(column.value.amount, column.value.currency, 0)}</span>}
            </header>
            <ol className="flex min-h-[72px] flex-col gap-2">
                {column.cards.map((card) => (
                    <li key={card.id}>
                        <DraggableCard card={card} onOpen={onOpen} />
                    </li>
                ))}
                {!column.cards.length && <li className="rounded-lg border border-dashed px-3 py-5 text-center text-xs text-muted-foreground">{emptyLabel}</li>}
            </ol>
        </section>
    );
}

function DraggableCard({ card, onOpen }: { card: Card; onOpen: (id: string) => void }) {
    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id, data: { card } });
    return (
        <div
            ref={setNodeRef}
            {...listeners}
            {...attributes}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(card.id)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onOpen(card.id);
                listeners?.onKeyDown?.(e as any);
            }}
            className={cn('cursor-grab rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing', isDragging && 'opacity-40')}
            data-testid="desk-card"
            data-tier={card.tier}
        >
            <LeadCardView card={card} />
        </div>
    );
}

/** One lead on the board: their room, who, how warm, what they looked at, what it is worth. */
export function LeadCardView({ card, lifted }: { card: Card; lifted?: boolean }) {
    const { t } = useTranslation();
    return (
        <article className={cn('overflow-hidden rounded-xl border bg-card text-start', lifted && 'w-[256px] shadow-lg')}>
            {card.roomImageUrl && (
                <img src={card.roomImageUrl} alt="" loading="lazy" draggable={false} className="aspect-[16/9] w-full border-b object-cover" />
            )}
            <div className="flex flex-col gap-2 p-3">
                <div className="flex items-start gap-2.5">
                    {!card.roomImageUrl && (
                        <span className="flex size-8 shrink-0 items-center justify-center rounded-full border bg-muted text-xs font-medium" aria-hidden>
                            {initialOf(card)}
                        </span>
                    )}
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{displayName(card)}</p>
                        <p className="truncate text-xs text-muted-foreground">{card.email}</p>
                    </div>
                    <LeadTierBadge tier={card.tier} score={card.score} className="shrink-0" />
                </div>

                {(card.products.length > 0 || card.areaM2 || card.value) && (
                    <div className="flex items-center justify-between gap-2">
                        <ThumbStack items={card.products.map((p) => ({ key: p.productId, src: p.thumbnailPath, alt: p.title }))} max={3} size="xs" />
                        <span className="truncate text-xs tabular-nums text-muted-foreground">
                            {[card.areaM2 ? t('LEAD_DESK.AREA', { value: Math.round(card.areaM2) }) : null, card.value ? formatCurrency(card.value.amount, card.value.currency, 0) : null]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    {card.requestKinds.map((kind) => (
                        <span key={kind} className="rounded-md border px-1.5 py-0.5 text-foreground">
                            {t(`LEAD_DESK.REQUEST.${kind.toUpperCase()}`, { defaultValue: kind })}
                        </span>
                    ))}
                    {card.audience === 'trade' && (
                        <span className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5">
                            <HardHat className="size-3" aria-hidden />
                            {t('LEAD_DESK.AUDIENCE_TRADE')}
                        </span>
                    )}
                    {card.nextStepAt && (
                        <span className="inline-flex items-center gap-1">
                            <CalendarClock className="size-3" aria-hidden />
                            <RelativeTime value={card.nextStepAt} />
                        </span>
                    )}
                    <span className="ms-auto">
                        <RelativeTime value={card.lastActiveAt ?? card.lastSeenAt} />
                    </span>
                </div>
            </div>
        </article>
    );
}
